/**
 * AI Model Pricing — typed client helpers.
 * Mirrors the response shapes from merryflix-backend/src/types/aiModelPricing.types.ts.
 */
import { adminApi } from "@/lib/adminApi";

export type ModelCategory = "text" | "image";
export type ModelStatus = "live" | "degraded" | "unavailable";

export interface ModelStatusEntry {
  modelId: string;
  category: ModelCategory | null;
  currentPricePerMTokensUsd?: number | null;
  currentPricePerImageUsd?: number | null;
  availableUntil: string | null;
  replacement: string | null;
  lastProbedAt: string | null;
  lastProbeOk: boolean | null;
  lastLatencyMs: number | null;
  daysSinceLastProbe: number | null;
  status: ModelStatus;
}

export interface ModelsStatusResponse {
  models: ModelStatusEntry[];
}

export interface ProbeHistoryPoint {
  probedAt: string;
  ok: boolean;
  latencyMs: number;
}

export interface TierRecommendation {
  tierName: string;
  currency: string;
  currentPriceCents: number;
  avgCostPerUserMonthUsd: number;
  targetMarginPercent: number;
  recommendedPriceCents: number;
  deltaCents: number;
  breakdown: {
    totalCostUsdLast30Days: number;
    distinctUsersWithCost: number;
    notes: string;
  };
}

export const aiModelPricingApi = {
  status: () => adminApi.get<ModelsStatusResponse>("/admin/ai-model-pricing/models/status"),
  history: (modelId: string, days = 30) =>
    adminApi.get<ProbeHistoryPoint[]>(
      `/admin/ai-model-pricing/models/probe-history?modelId=${encodeURIComponent(modelId)}&days=${days}`
    ),
  probeNow: (modelId: string) =>
    adminApi.post(`/admin/ai-model-pricing/models/${encodeURIComponent(modelId)}/probe-now`, {}),
  recommendations: () =>
    adminApi.get<{ recommendations: TierRecommendation[] }>(
      "/admin/ai-model-pricing/pricing/recommendations"
    ),
  updateMargin: (tierName: string, targetMarginPercent: number) =>
    adminApi.patch("/admin/ai-model-pricing/pricing/margin", {
      tierName,
      targetMarginPercent,
    }),
  guide: (tier: string) =>
    adminApi
      .get<{ markdown: string }>(
        `/admin/ai-model-pricing/pricing/guide?tier=${encodeURIComponent(tier)}`
      )
      .then((r) => r.markdown),
};
